import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import {
  useHRExportWorkTimeReportHook,
  useHRGetWorkTimeReport,
} from 'api/hr/hr';
import {
  UserModel,
  WorkTimeReportAvailabilityDayDTO,
  WorkTimeReportRowDTO,
} from 'api/model';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import Button from 'components/inputs/Button';
import Checkbox from 'components/inputs/Checkbox';
import Input from 'components/inputs/Input';
import SectionHeader from 'components/SectionHeader';
import TransparentSpinner from 'components/spinners/TransparentSpinner';
import UserPicker from 'components/UserPicker';
import useUsers from 'contexts/basicData/useUsers';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';
import { downloadBlob } from 'utils/file-utils';
import {
  formatHours,
  matchesEmploymentId,
  normalizeWorkTimeReport,
} from './workTimeReportUtils';

// HR > Tidsrapport. Urvalet och lönetiden räknas i backend (WorkTimeReportLogic, samma regler
// som löneexporten); sidan visar bara resultatet. Se .claude/features/work-time-report.md.

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px 0;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 15px;
`;

const Field = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PickerWrapper = styled.div`
  width: 300px;
`;

const Info = styled.p`
  margin: 0;
  max-width: 900px;
  color: ${({ theme }) => theme.colors.foreground.tertiary};
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;

  strong {
    font-size: ${({ theme }) => theme.sizes.font.medium};
  }
`;

const NotInSalary = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground.error};
`;

const ReportTable = styled(Table)`
  .wtr-not-in-salary {
    color: ${({ theme }) => theme.colors.foreground.error};
  }
` as typeof Table;

const DeviationReports = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  white-space: normal;
`;

const ruleText =
  'Tider i timmar med decimaler (1,25 är 1 timme och 15 minuter). ' +
  'Till löneunderlag räknas som i löneexporten: godkända uppdrag med start- och sluttid. ' +
  'Har uppdraget fast tid betalas den fasta tiden, oavsett hur länge uppdraget tog, och rasten ' +
  'dras inte av från den. Annars betalas arbetad tid minus rast (eller beviljad standbytid minus ' +
  'rast). Arbetad tid är sluttid minus starttid, med rasten kvar. Delta är arbetad tid minus ' +
  'det som går till löneunderlaget.';

const availabilityText =
  'Tillgängligheten som den syns i tillgänglighetsvyn. Lagd är när en inlagd dag skapades. ' +
  'Senast ändrad är när dagen senast sparades om, av fälttestaren, TL eller den som beviljade ' +
  'frånvaron; bara den senaste ändringen sparas.';

const availabilityColumns: ColumnSetting<
  WorkTimeReportAvailabilityDayDTO,
  any
>[] = [
  { head: 'Datum', render: (d) => d.date, width: 95 },
  { head: 'Veckodag', render: (d) => d.weekday, width: 90 },
  { head: 'Tillgänglighet', render: (d) => d.availability, width: 200 },
  { head: 'Från', render: (d) => d.start ?? '', width: 55 },
  { head: 'Till', render: (d) => d.end ?? '', width: 55 },
  { head: 'Källa', render: (d) => d.source, width: 120 },
  {
    head: 'Lagd',
    render: (d) => [d.placed, d.placedBy].filter(Boolean).join(', '),
    width: 200,
  },
  {
    head: 'Senast ändrad',
    render: (d) => [d.lastChanged, d.lastChangedBy].filter(Boolean).join(', '),
    width: 200,
  },
  { head: 'Kommentar', render: (d) => d.comment ?? '', width: 200 },
];

const findByEmploymentId = (
  users: ReturnType<typeof useUsers>,
  query: string
) =>
  Object.values(users).filter((u) =>
    matchesEmploymentId(u.employmentID, query)
  );

const sumMinutes = (
  rows: WorkTimeReportRowDTO[],
  pick: (r: WorkTimeReportRowDTO) => number | undefined
) => rows.reduce((sum, r) => sum + (pick(r) ?? 0), 0);

const WorkTimeReportPage = () => {
  const users = useUsers();
  const [userId, setUserId] = useState<number | null>(null);
  const [employmentId, setEmploymentId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [onlyFixedTime, setOnlyFixedTime] = useState(true);

  const params = {
    userId: userId ?? 0,
    from: from || undefined,
    to: to || undefined,
    onlyFixedTime,
  };
  const report = useHRGetWorkTimeReport(params, {
    query: { enabled: userId !== null },
  });
  const data = useMemo(
    () => (report.data ? normalizeWorkTimeReport(report.data) : undefined),
    [report.data]
  );

  const exportReport = useHRExportWorkTimeReportHook();
  // Parametrarna skickas med i run(): useApiCallFn memoiserar anroparen, så värden som fångas i
  // closuren blir kvar från första renderingen (samma fälla som på LAS-dagar).
  const exportCall = useApiCallFn(
    (p: typeof params) => exportReport(p),
    [exportReport]
  );

  const handleUserPicked = (user: UserModel | null) => {
    setUserId(user?.userID ?? null);
    setEmploymentId(user?.employmentID ?? '');
  };

  // Anst.nr måste peka ut en person, annars väljs ingen (samma regel som backend).
  const employmentIdMatches = useMemo(
    () => findByEmploymentId(users, employmentId),
    [users, employmentId]
  );

  // Pekar numret inte ut exakt en person släpps det tidigare valet, så att förra personens
  // rapport inte står kvar (och kan laddas ner) under ett nummer som tillhör någon annan.
  // Ett tömt fält behåller valet: det töms också när man väljer i namnlistan utan nummer.
  const handleEmploymentIdChange = (value: string) => {
    setEmploymentId(value);
    if (!value.trim()) return;
    const matches = findByEmploymentId(users, value);
    setUserId(matches.length === 1 ? matches[0].userID : null);
  };

  const handleExport = async () => {
    if (userId === null || !data) return;
    const [result] = await exportCall.run(params);
    const name = [data.name, data.employmentID].filter(Boolean).join(' ');
    if (result) downloadBlob(result, `Tidsrapport ${name}.xlsx`);
  };

  const rows = data?.rows ?? [];
  const availability = data?.availability ?? [];
  const notInSalaryCount = rows.filter((r) => !r.inSalaryBasis).length;

  const columns: ColumnSetting<WorkTimeReportRowDTO, any>[] = [
    {
      head: 'Datum',
      render: (r) => r.date,
      width: 95,
      sortFunction: (a, b) => a.date.localeCompare(b.date),
    },
    { head: 'Regnr', render: (r) => r.registrationNumber, width: 75 },
    { head: 'Uppdrag', render: (r) => r.assignmentID, width: 75 },
    { head: 'Status', render: (r) => r.status, width: 95 },
    { head: 'Start', render: (r) => r.startTime?.slice(11) ?? '', width: 55 },
    { head: 'Slut', render: (r) => r.endTime?.slice(11) ?? '', width: 55 },
    {
      head: 'Estimerad',
      render: (r) => formatHours(r.estimatedMinutes),
      width: 80,
    },
    {
      head: 'Arbetad',
      render: (r) => formatHours(r.workedMinutes),
      width: 70,
      sortFunction: (a, b) => (a.workedMinutes ?? 0) - (b.workedMinutes ?? 0),
    },
    {
      head: 'Fast tid',
      render: (r) => formatHours(r.fixedMinutes),
      width: 70,
    },
    { head: 'Rast', render: (r) => formatHours(r.breakMinutes), width: 55 },
    {
      head: 'Till lön',
      render: (r) => formatHours(r.salaryMinutes),
      width: 70,
    },
    {
      head: 'Delta',
      render: (r) => formatHours(r.deltaMinutes),
      width: 65,
      sortFunction: (a, b) => (a.deltaMinutes ?? 0) - (b.deltaMinutes ?? 0),
    },
    {
      head: 'Varav OB',
      render: (r) => formatHours(r.overtimeMinutes),
      width: 75,
    },
    {
      head: 'Löneunderlag',
      render: (r) =>
        r.inSalaryBasis ? (
          'Ja'
        ) : (
          <NotInSalary>Nej: {r.salaryBasisNote}</NotInSalary>
        ),
      width: 220,
    },
    {
      head: 'Avvikelserapporter',
      render: (r) => (
        <DeviationReports>
          {r.deviationReports.map((d, i) => (
            // eslint-disable-next-line react/no-array-index-key -- listan har ingen egen nyckel och ändrar aldrig ordning
            <span key={i}>
              {d.created}
              {d.reasons.length > 0 && ` ${d.reasons.join(', ')}`}
              {d.message && `: ${d.message}`}
            </span>
          ))}
        </DeviationReports>
      ),
      width: 280,
    },
  ];

  return (
    <Wrapper>
      <Info>{ruleText}</Info>

      <Section>
        <Toolbar>
          <PickerWrapper>
            <UserPicker
              selectedUserId={userId}
              onUserPicked={handleUserPicked}
              noneOption="Välj fälttestare"
            />
          </PickerWrapper>
          <Field>
            Anst.nr
            <Input
              width={90}
              value={employmentId}
              onChange={(e) => handleEmploymentIdChange(e.currentTarget.value)}
            />
          </Field>
          <Field>
            Från
            <Input
              type="date"
              width={150}
              value={from}
              onChange={(e) => setFrom(e.currentTarget.value)}
            />
          </Field>
          <Field>
            Till
            <Input
              type="date"
              width={150}
              value={to}
              onChange={(e) => setTo(e.currentTarget.value)}
            />
          </Field>
          <Checkbox
            checked={onlyFixedTime}
            onChange={() => setOnlyFixedTime((v) => !v)}
          >
            Bara uppdrag med fast tid
          </Checkbox>
        </Toolbar>
        {employmentId.trim() !== '' &&
          employmentIdMatches.length !== 1 &&
          Object.keys(users).length > 0 && (
            <Info>
              {employmentIdMatches.length === 0
                ? 'Ingen fälttestare har det anställningsnumret.'
                : 'Flera konton har det anställningsnumret. Välj personen i listan.'}
            </Info>
          )}
      </Section>

      {userId !== null && (
        <Section>
          <SectionHeader>
            {data
              ? `${data.name}${data.employmentID ? ` (${data.employmentID})` : ''}`
              : report.isError
                ? 'Kunde inte hämta tidsrapporten'
                : 'Laddar…'}
          </SectionHeader>
          {report.isLoading && <TransparentSpinner />}
          {report.isError && (
            <Toolbar>
              <Info>Tidsrapporten kunde inte hämtas.</Info>
              <Button onClick={() => report.refetch()}>Försök igen</Button>
            </Toolbar>
          )}
          {data && (
            <>
              <Summary>
                <span>
                  Uppdrag: <strong>{rows.length}</strong>
                </span>
                <span>
                  Arbetad tid:{' '}
                  <strong>
                    {formatHours(sumMinutes(rows, (r) => r.workedMinutes))}
                  </strong>
                </span>
                <span>
                  Fast tid:{' '}
                  <strong>
                    {formatHours(sumMinutes(rows, (r) => r.fixedMinutes))}
                  </strong>
                </span>
                <span>
                  Rast:{' '}
                  <strong>
                    {formatHours(sumMinutes(rows, (r) => r.breakMinutes))}
                  </strong>
                </span>
                <span>
                  Till löneunderlag:{' '}
                  <strong>
                    {formatHours(sumMinutes(rows, (r) => r.salaryMinutes))}
                  </strong>
                </span>
                <span>
                  Delta:{' '}
                  <strong>
                    {formatHours(sumMinutes(rows, (r) => r.deltaMinutes))}
                  </strong>
                </span>
                <span>
                  Varav OB:{' '}
                  <strong>
                    {formatHours(sumMinutes(rows, (r) => r.overtimeMinutes))}
                  </strong>
                </span>
                {notInSalaryCount > 0 && (
                  <NotInSalary>
                    {notInSalaryCount} uppdrag ej med i löneunderlaget
                  </NotInSalary>
                )}
              </Summary>
              {data.isFullTime && (
                <Info>
                  Personen är markerad som heltidsanställd och följer inte med
                  Hogia-exporten (timlön).
                </Info>
              )}
              <Toolbar>
                <Button
                  onClick={handleExport}
                  disabled={exportCall.status === RequestStatus.Fetching}
                >
                  <FontAwesomeIcon icon={faDownload} /> Ladda ner Excel
                </Button>
              </Toolbar>
              <ReportTable
                columnSettings={columns}
                rows={rows}
                // Table använder klassnamnet som React-nyckel, så uppdrags-id:t måste med.
                rowClassName={(r) =>
                  `wtr-row-${r.assignmentID}${
                    r.inSalaryBasis ? '' : ' wtr-not-in-salary'
                  }`
                }
              />
              <SectionHeader>Tillgänglighet under perioden</SectionHeader>
              <Info>{availabilityText}</Info>
              <Table
                columnSettings={availabilityColumns}
                rows={availability}
                rowClassName={(d) => `wtr-day-${d.date}`}
              />
            </>
          )}
        </Section>
      )}
    </Wrapper>
  );
};

export default WorkTimeReportPage;
